import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import api from '../../../../utils/api'
import Utils from '../../../../utils/utils'
import { __STORAGE } from '../../../../utils/enums'
import Routes from '../../../../utils/routes'
import { launchAppPreload } from '../../../../redux/actions'
import { setLoggedIn } from '../../../../redux/slices/app'
import {
    exchangeCodeForTokens,
    validateCallbackState,
    clearSocialLoginSession,
    getEmailFromToken,
    getSocialProvider,
    setLastSocialProvider,
} from '../../../../services/keycloakAuth'

export function useCompleteSocialLogin() {
    const dispatch = useDispatch()
    const history = useHistory()

    return useCallback(async ({
        code,
        state,
        demo,
        callbackPath,
    }: {
        code: string
        state: string | null
        demo?: boolean
        callbackPath?: string
    }) => {
        try {
            if (!validateCallbackState(state)) {
                throw new Error('Invalid state parameter')
            }

            const tokens = await exchangeCodeForTokens(code, callbackPath)

            if (!tokens.access_token || !tokens.refresh_token) {
                throw new Error('Invalid tokens received')
            }

            const refreshExpiresIn = tokens.refresh_expires_in > 0 ? tokens.refresh_expires_in : 3600
            api.setAuthTokens({
                accessToken: tokens.access_token,
                refreshToken: tokens.refresh_token,
                idToken: tokens.id_token,
                refreshTokenValidUntil: new Date(Date.now() + refreshExpiresIn * 1000).toISOString(),
            })

            api.trackAttribution({
                surface: 'thinkportal',
                landing_url: sessionStorage.getItem(__STORAGE.utmLink) || undefined,
                _fbp: Utils.getCookie('_fbp'),
                _fbc: Utils.getCookie('_fbc'),
            })

            const idToken = tokens.id_token || tokens.access_token
            const email = getEmailFromToken(idToken)
            const provider = getSocialProvider()

            if (provider) {
                setLastSocialProvider(provider)
            }

            // Demo defaults to needing completion (extra form step); live defaults to not.
            let needsCompletion = demo ?? false
            try {
                const profileStatus = await api.checkProfileStatus()
                needsCompletion = profileStatus.needsCompletion
            } catch (err) {
                console.error('Profile status check failed:', err)
            }

            if (needsCompletion) {
                history.replace(demo
                    ? Routes.account.createAccount.demo
                    : Routes.account.socialRegistration
                )
            } else {
                if (email) {
                    Utils.saveEncryptedString(__STORAGE.user, email.toLowerCase())
                }
                dispatch(setLoggedIn(true))
                dispatch(launchAppPreload())
            }
        } finally {
            clearSocialLoginSession()
        }
    }, [dispatch, history])
}
